import { FileWarning } from "lucide-react";

import { useListUnmatchedPatientUploads } from "@elation/api-client/src/stitch-web";
import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { pluralize } from "@src/shared/utils/strings";

import { CareProtocolOpportunityEntity } from "../../../../../domain";
import { CareProtocolOpportunityListSkeleton } from "./CareProtocolOpportunityListSkeleton";

function unmatchedReasonLabel(
  reason: UnmatchedPatientUpload["reason"],
): string {
  if (reason === "insufficient_identifiers") {
    return "Missing required patient identifiers";
  }
  return "No patient matched DOB and last name";
}

function identifierSummaryLabel(
  summary: UnmatchedPatientUpload["identifierSummary"],
): string {
  if (!summary) return "Identifier details unavailable";

  const fields = [
    summary.hasFirstName ? "first name" : null,
    summary.hasMiddleName ? "middle name" : null,
    summary.hasLastName ? "last name" : null,
    summary.hasDob ? "DOB" : null,
    summary.hasSex ? "sex" : null,
    summary.hasPhone ? "phone" : null,
    summary.hasEmail ? "email" : null,
    summary.hasAddress ? "address" : null,
    summary.hasSsnLast4 ? "SSN last 4" : null,
  ].filter(Boolean);

  if (fields.length === 0) return "No usable identifiers found";
  return `Found ${fields.join(", ")}`;
}

export function UnmatchedPatientUploadsList() {
  const query = useListUnmatchedPatientUploads(
    { limit: 50 },
    {
      query: {
        refetchInterval: 5_000,
      },
    },
  );
  const documents = query.data?.data.documents ?? [];

  if (query.isLoading) return <CareProtocolOpportunityListSkeleton />;

  if (query.isError) {
    return (
      <div className="rounded-[12px] border border-border bg-background px-4 py-5 text-[13px] text-muted-foreground">
        Unable to load unmatched documents.
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="rounded-[12px] border border-border bg-background px-4 py-5 text-[13px] text-muted-foreground">
        No unmatched documents.
      </div>
    );
  }

  return (
    <ul aria-label="Unmatched patient documents" className="space-y-3">
      {documents.map((item) => (
        <li
          key={item.document.id}
          className="overflow-hidden rounded-[12px] border border-border bg-background shadow-none"
        >
          <HorizontalFlexbox
            alignItems="center"
            className="gap-[11px] bg-muted/35 px-3.5 py-3"
          >
            <div className="flex size-[30px] shrink-0 items-center justify-center rounded-[8px] bg-destructive/10 text-destructive">
              <FileWarning className="size-[15px]" aria-hidden="true" />
            </div>
            <VerticalFlexbox className="min-w-0 flex-1">
              <span className="truncate text-[13.5px] font-semibold text-surface-foreground">
                {item.document.fileName}
              </span>
              <span className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">
                {[
                  item.document.uploadedAt
                    ? CareProtocolOpportunityEntity.uploadTimeLabel(
                        item.document.uploadedAt,
                      )
                    : "Upload date unknown",
                  item.document.mimeType,
                ].join(" · ")}
              </span>
            </VerticalFlexbox>
          </HorizontalFlexbox>
          <VerticalFlexbox className="gap-1 border-t border-border px-4 py-3">
            <span className="text-[12.5px] text-surface-foreground">
              {unmatchedReasonLabel(item.reason)}
            </span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {identifierSummaryLabel(item.identifierSummary)}
            </span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {pluralize(item.candidateCount, "candidate")} found
            </span>
          </VerticalFlexbox>
        </li>
      ))}
    </ul>
  );
}
